import { SelectedFilters } from '@lidea/shared/data-access/core';
import { onlineManager } from '@tanstack/react-query';
import { useMemo, useSyncExternalStore } from 'react';

import {
  getOfflineScopeSession,
  OFFLINE_SCOPE_CHANGED_EVENT,
} from './sync-job/offline-scope-session';

function subscribeOfflineScope(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener(OFFLINE_SCOPE_CHANGED_EVENT, handler);
  return () => window.removeEventListener(OFFLINE_SCOPE_CHANGED_EVENT, handler);
}

function subscribeOnline(onStoreChange: () => void) {
  return onlineManager.subscribe(() => onStoreChange());
}

/** Apply CDS scope manager filter when offline so list/map match synced perimeter. */
export function mergeOfflineScopeIntoFilters(
  filters: SelectedFilters
): SelectedFilters {
  const scope = getOfflineScopeSession();
  if (!scope) {
    return filters;
  }
  return {
    ...filters,
    manager: [String(scope.managerId)],
  };
}

/**
 * Plot list / map filters: when offline and a CDS scope is set, force manager filter
 * so React Query reads the same cache key as the offline sync job.
 */
export function useOfflineAwarePlotFilters(
  baseFilters: SelectedFilters
): SelectedFilters {
  const isOnline = useSyncExternalStore(
    subscribeOnline,
    () => onlineManager.isOnline(),
    () => true
  );
  useSyncExternalStore(
    subscribeOfflineScope,
    () => getOfflineScopeSession()?.managerId ?? null,
    () => null
  );

  return useMemo(() => {
    if (isOnline) {
      return baseFilters;
    }
    return mergeOfflineScopeIntoFilters(baseFilters);
  }, [isOnline, baseFilters]);
}
